


import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";

import BooleanField from "Web/Components/Common/Input/Boolean.vue";
import Form from "Web/Components/Form/Index.vue";
import FormField from "Web/Components/Form/Field.vue";
import PopupWindow from "Web/Components/Popup/Window.vue";
import FormFieldset from "Web/Components/Form/Fieldset.vue";

import { AdsPublications } from "App";

import { $t, EventBus } from "App/IOC";
import { CountDifferentFields } from "App/Externals/Services/Base";

import {
    CustomNotification,
    Field,
    PagerSorter,
    TextFilter
} from "App/Models";

import {
    Activated,
    Dictionary,
    Identifiable,
    Named
} from "App/Contracts";
import { CustomError } from "@IBS/Frontend/Models";

/* Dedicated form for API-type gateways (Amepi). The user only sets a name, a username and a
   password, then attributes groups. Create/read/update route to /gateway-api/* through the
   *Api facade methods. Group attribution reuses the shared CustomerCreate/DeleteCustomer
   endpoints (type-agnostic), exactly like the FTP form. */
@Component({
    components: {
        BooleanField,
        Field: FormField,
        Fieldset: FormFieldset,
        Form,
        PopupWindow
    }
})
export default class GatewayApiForm extends Vue {
    @Prop() readonly row?: any;

    /* Inline (full-page) vs modal, same convention as the FTP form. */
    @Prop( { default: false } ) readonly FullPage!: boolean;

    @Ref( "form" )
    private readonly form?: Form;

    private fields: Dictionary<Field> = {};

    /* API portals label credentials "Identifiant" / "Mot de passe", not the FTP wording reused
       by AdsPublications.Fields. Fresh Field objects (not the shared cached ones) so the FTP
       form keeps its own labels. Same Ids (Username/Password) keep the record mapping identical. */
    private get usernameField(): Field {
        return Field.Text( "Username", $t( "ADS_PUBLICATION_fieldApiUsername" ) );
    }

    private get passwordField(): Field {
        return Field.Text( "Password", $t( "ADS_PUBLICATION_fieldApiPassword" ) );
    }

    private action: Identifiable & Named & Activated = {
        Id: "create",
        Name: $t( "ADS_PUBLICATION_confirmCreate" ),
        Active: true
    };
    private record: any = {};
    private fileObj: any = null;
    private imageUrl: string = "";
    private isImageDeleted: boolean = false;
    private allowSold: boolean = false;

    private async beforeCreate(): Promise<void> {
        this.fields = await AdsPublications.Fields.Get();
    }

    private async onFormSubmit(): Promise<void> {
        if( this.form === undefined )
            return;

        this.action.Active = false;

        const record: any | undefined = this.form.GetRecord() as any;

        if(this.row !== undefined && this.record !== undefined
            && this.row !== null && this.row.Id !== undefined) {
            await this.update(record);
        }
        else {
            await this.create(record);
        }
    }

    private async fileToBase64(file: any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    private toNumericBoolean(value: any): number {
        if(value === undefined || value === null)
            return 0;

        if(typeof value === "string")
            return value === "1" || value.toLowerCase() === "true" ? 1 : 0;

        if(typeof value === "number")
            return value === 1 ? 1 : 0;

        return value === true ? 1 : 0;
    }

    private get allowSoldValue(): boolean {
        return this.allowSold;
    }

    private onAllowSoldChange(value: boolean): void {
        this.allowSold = value;

        /* Toggle hors des FormField : sans ca son changement n'entre pas dans le record
           du Form et la barre Enregistrer ne se declenche pas. On l'injecte pour dirty. */
        if( this.form !== undefined )
            this.form.SetValue( "allowSold", value );
    }

    private async create(record: any): Promise<void> {
        if(this.fileObj) {
            const base64 = await this.fileToBase64(this.fileObj);
            record.File = base64;
        }

        record.allowSold = this.allowSold;
        const [ createdRecord, error ] = await AdsPublications.CreateApi( record );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            this.action.Active = true;
            this.canCreate();

            return;
        }

        EventBus.Publish(
            "Notification",
            CustomNotification.Success(
                $t( "ADS_PUBLICATION_createSuccessTitle" ),
                $t( "ADS_PUBLICATION_createSuccessMessage" )
            )
        );

        const createdData: any = createdRecord as any;
        if( createdData !== undefined && createdData.id !== undefined ) {
            await this.syncGroups(record, createdData.id as number );
        }
        this.$emit( "Create", createdRecord );
    }

    private async syncGroups(record: any, id: number, isUpdate: boolean = false): Promise<void> {
        const differentFields = CountDifferentFields(this.record, record);
        if( Object.keys( differentFields as {} ).length === 0
        || differentFields.Customers === undefined ) {
            EventBus.Publish(
                "Notification",
                CustomNotification.Success(
                    $t( "ADS_PUBLICATION_createSuccessTitle" ),
                    $t( "GLOBAL_nothingToUpdate" )
                )
            );
            return;
        }

        // eslint-disable-next-line max-len
        if(isUpdate && differentFields.Customers !== undefined
        && differentFields.Customers.length === 0
        && this.record.Customers !== undefined
        && this.record.Customers.length > 0) {
            for(const item of this.record.Customers) {
                const data: any = { Id: id, GroupId: item.Group };
                // eslint-disable-next-line max-len
                const [ , error ] = await AdsPublications.DeleteCustomer(this.record.Id, data );
                if( error !== undefined ) {
                    EventBus.Publish( "Error", error );
                    this.action.Active = true;
                    this.canCreate();
                }
            }
        }
        if(differentFields.Customers !== undefined && differentFields.Customers.length > 0) {
            if(isUpdate && this.record.Customers !== undefined
            && this.record.Customers.length > 0) {
                for(const item of this.record.Customers) {
                    const data: any = { Id: id, GroupId: item.Group };
                    // eslint-disable-next-line max-len
                    const [ , error ] = await AdsPublications.DeleteCustomer(this.record.Id, data );
                    if( error !== undefined ) {
                        EventBus.Publish( "Error", error );
                        this.action.Active = true;
                        this.canCreate();
                    }
                }
            }

            for(const item of record.Customers) {
                const data: any = { Id: id, GroupId: item.Group };
                const [ , error ] = await AdsPublications.CustomerCreate( data);

                if( error !== undefined ) {
                    EventBus.Publish( "Error", error );
                    this.action.Active = true;
                    this.canCreate();
                }
            }

            EventBus.Publish(
                "Notification",
                CustomNotification.Success(
                    $t( "ADS_PUBLICATION_createSuccessTitle" ),
                    isUpdate ? $t( "ADS_PUBLICATION_UpdateCustomerMessage" )
                        : $t( "ADS_PUBLICATION_createCustomerMessage" )
                )
            );
        }
    }

    private async update(record: any): Promise<void> {
        if(this.fileObj) {
            const base64 = await this.fileToBase64(this.fileObj);
            record.File = base64;
            if(this.record.Image) {
                const [ , error ] = await AdsPublications.DeletePicture( this.record.Id );
                if( error !== undefined ) {
                    EventBus.Publish( "Error", error );
                }
            }
        } else if(this.isImageDeleted) {
            const [ , error ] = await AdsPublications.DeletePicture( this.record.Id );
            if( error !== undefined ) {
                EventBus.Publish( "Error", error );
            }
            record.File = undefined;

            const differentFields = CountDifferentFields(this.record, record);
            if( Object.keys( differentFields as {} ).length === 0 ) {
                EventBus.Publish(
                    "Notification",
                    CustomNotification.Success(
                        $t( "ADS_PUBLICATION_createSuccessTitle" ),
                        $t( "ADS_PUBLICATION_createUpdateMessage" )
                    )
                );

                this.$emit( "Create", record );
                return;
            }
        }
        record.allowSold = this.allowSold;

        const [ createdRecord, error ] = await AdsPublications.UpdateApi( this.record, record );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            this.action.Active = true;
            this.canCreate();

            return;
        }

        EventBus.Publish(
            "Notification",
            CustomNotification.Success(
                $t( "ADS_PUBLICATION_createSuccessTitle" ),
                $t( "ADS_PUBLICATION_createUpdateMessage" )
            )
        );

        await this.syncGroups(record, this.record.Id as number, true );
        this.$emit( "Create", createdRecord );
    }

    private canCreate(): void {
        if( this.form === undefined )
            return;

        this.action.Active = true;
    }

    @Watch( "row", { immediate: true } )
    private async onRowChanged(): Promise<void> {
        if( this.row === undefined || this.row === null || this.row === "" ) {
            this.record = {};
            this.allowSold = false;
            return;
        }
        const id = this.row.Id ? this.row.Id : this.row.id;
        this.action.Name = id !== undefined && id !== "" ?
            $t( "ADS_PUBLICATION_confirmUpdate" ) : $t( "ADS_PUBLICATION_confirmCreate" );
        const [ record, error ] = await AdsPublications.GetApi( id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            this.action.Active = true;
            this.canCreate();

            return;
        }

        const formatedRecord = this.capitalizeKeys( record );

        const rawRecord: any = record;
        if(rawRecord !== undefined) {
            let allowSource;
            if( rawRecord.allow_sold !== undefined ) {
                allowSource = rawRecord.allow_sold;
            } else if( rawRecord.allowSold !== undefined ) {
                allowSource = rawRecord.allowSold;
            } else if( rawRecord.Allow_sold !== undefined ) {
                allowSource = rawRecord.Allow_sold;
            } else {
                allowSource = rawRecord.AllowSold;
            }
            this.allowSold = this.toNumericBoolean( allowSource ) === 1;
        } else {
            this.allowSold = false;
        }

        /* Une seule cle allowSold dans le record. Sinon capitalizeKeys/Get laissent aussi AllowSold
           et Allow_sold : cote backend appendValues lowercase tout -> deux "allowsold" en conflit,
           et .get() prend la stale (false) au lieu du toggle. On purge les variantes et on pose la
           baseline en camelCase, seule cle envoyee au backend. */
        delete formatedRecord.AllowSold;
        delete formatedRecord.Allow_sold;
        formatedRecord.allowSold = this.allowSold;

        if(formatedRecord.Password !== undefined) {
            formatedRecord.Password = { Value: formatedRecord.Password };
        }

        if(formatedRecord.Customers !== undefined && formatedRecord.Customers.length > 0) {
            formatedRecord.Customers = formatedRecord.Customers.map(function(item: any) {
                return { Group: item.group, Id: item.id, Name: item.name };
            });
        }

        this.record = formatedRecord;

        if(this.record !== undefined && this.record.Image !== undefined) {
            this.imageUrl = this.record.Image;
        }
    }

    private capitalizeKeys(obj: any): Record<string, any> {
        const newObj: Record<string, any> = {};
        for(const key in obj) {
            if(obj.hasOwnProperty(key)) {
                const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
                newObj[capitalizedKey] = obj[key];
            }
        }
        return newObj;
    }

    private async handleImageUpload(event: any): Promise<void> {
        const file = event.target.files[0];
        if(!file) return;
        this.fileObj = file;
        this.imageUrl = URL.createObjectURL(file);
    }

    private async handleImageDelete(): Promise<void> {
        if(this.record && this.record.Id && this.record.Image) {
            EventBus.Publish("Confirmation", {
                Props: {
                    Title: this.$t("GLOBAL_attention"),
                    Message: this.$tc("ADS_PUBLICATION_deleteImageConfirm"),
                    Actions: [{
                        Id: "endTrial",
                        Name: this.$t("GLOBAL_actionDelete"),
                        Active: true
                    }]
                },
                Events: {
                    Action: async () => {
                        this.fileObj = null;
                        this.imageUrl = "";
                        this.isImageDeleted = true;
                    }
                }
            });
        } else {
            this.fileObj = null;
            this.imageUrl = "";
        }
    }

    private async getCustomers( keyword?: string ): Promise<[ any[], CustomError | undefined ]> {
        const filters: TextFilter[] = [];

        if( keyword !== undefined && keyword !== "" ) {
            const filter = new TextFilter( "Name", "" );
            filter.Value = [ keyword ];
            filters.push( filter );
        }

        const [ result, , error ] =
        await AdsPublications.GetAllCustomers( filters, new PagerSorter( 999 ) );

        return [ result, error ];
    }
}
